import { useMemo } from "react";
import { useAppDispatch } from "Redux/store";
import { useSelector } from "react-redux";

import {
  AsyncLoadOptionType,
  AsyncLoadOptionTypeEnum,
} from "Components/UI/Select/types";
import { selectVehicleList } from "Redux/Vehicle/slice";
import { selectVehicleVendorList, selectVendorList } from "Redux/Vendors/slice";
import { selectContactList, selectUserList } from "Redux/User/slice";
import { selectExpenseTypeList } from "Redux/Expense/slice";
import { selectTelematiceDeviceList } from "Redux/TelematicDevice/slice";
import { selectLabelsTypeList } from "Redux/LabelsType/LabelsTypeSlice";
import { selectRoleList } from "Redux/Role/slice";
import { selectGroupTypeList } from "Redux/GroupType/GroupTypeSlice";
import { selectOwnerShipGroupList } from "Redux/OwnerShipType/OwnerShipGroupSlice";
import { getOwnerShipGroupList } from "Redux/OwnerShipType/OwnerShipTypeReducer";
import {
  selectEquipmentTypes,
  selectUsers,
  selectVehicles,
} from "Redux/Equipment/slice";
import {
  selectAssemblyCodes,
  selectMaintenanceCategories,
  selectReasonForRepairCodes,
  selectServiceTasksList,
  selectSystemCodes,
} from "Redux/ServiceTask/slice";
import {
  selectPartManufacture,
  selectPartsLocations,
  selectPartsMeasurements,
} from "Redux/Parts/slice";
import { selectTypeList } from "Redux/Type/typeSlice";
import { selectModelTypeList } from "Redux/ModelType/ModelTypeSlice";
import { selectMakeTypeList } from "Redux/MakeType/MakeTypeSlice";
import { selectFuelTypeList } from "Redux/FuelType/FuelTypeSlice";
import { selectPaymentTypeList } from "Redux/PaymentType/slice";
import { selectReminderContactTypes } from "Redux/ReminderContact/slice";
import { selectFaultsCodesList } from "Redux/Faults/slice";
import { getVehicleList } from "Redux/Vehicle/reducer";
import { getPaymentType } from "Redux/PaymentType/reducer";
import { getContactList, getUserList } from "Redux/User/reducer";
import { getVehicleVendorsList, getVendorsList } from "Redux/Vendors/reducer";
import { getLabelsTypeList } from "Redux/LabelsType/LabelsTypeReducer";
import { getExpenseTypeList } from "Redux/Expense/reducer";
import { getTelematicDeviceList } from "Redux/TelematicDevice/reducer";
import { getGroupTypeList } from "Redux/GroupType/GroupTypeReducer";
import { getRolesList } from "Redux/Role/reducer";
import {
  getAssemblyCodes,
  getMaintenanceCategories,
  getReasonForRepairCodes,
  getServiceTasksList,
  getSystemCodes,
} from "Redux/ServiceTask/reducer";
import {
  getEquipmentTypes,
  getUsers,
  getVehicles,
} from "Redux/Equipment/reducer";
import {
  getMeasurements,
  getPartManufactures,
  getPartsCategories,
  getPartsLocations,
} from "Redux/Parts/reducers";
import { getTypeList } from "Redux/Type/typeReducer";
import { getModelList } from "Redux/ModelType/ModelTypeReducer";
import { getMakeTypeList } from "Redux/MakeType/MakeTypeReducer";
import { getFuelTypeList } from "Redux/FuelType/FuelTypeReducer";
import { getReminderConcactTypes } from "Redux/ReminderContact/reducer";
import { getFaultCodesList } from "Redux/Faults/reducer";

export type TAsyncLoadOptionsParams = {
  search?: string;
  limit: 10;
  offset: number;
};

export type useRequestsProps = {
  asyncLoadOptionType: AsyncLoadOptionType | undefined;
};

export const useRequests = ({ asyncLoadOptionType }: useRequestsProps) => {
  const dispatch = useAppDispatch();

  // Selectors
  const vehicleList = useSelector(selectVehicleList);
  const vendorList = useSelector(selectVendorList);
  const userList = useSelector(selectUserList);
  const expenseTypeList = useSelector(selectExpenseTypeList);
  const telematiceDevices = useSelector(selectTelematiceDeviceList);
  const labelDevices = useSelector(selectLabelsTypeList);
  const roles = useSelector(selectRoleList);
  const group = useSelector(selectGroupTypeList);
  const ownerShipGroup = useSelector(selectOwnerShipGroupList);
  const equipmentTypeList = useSelector(selectEquipmentTypes);
  const serviceTaskList = useSelector(selectServiceTasksList);
  const partCategoryList = useSelector(selectPartsMeasurements).measurements;
  const partManufacturerList = useSelector(selectPartManufacture);
  const vehicleTypeList = useSelector(selectTypeList) || [];
  const vehicleModelList = useSelector(selectModelTypeList) || [];
  const vehicleMakeList = useSelector(selectMakeTypeList) || [];
  const fuelTypeList = useSelector(selectFuelTypeList) || [];
  const vehicleVendorList = useSelector(selectVehicleVendorList) || [];
  const contacts = useSelector(selectContactList) || [];
  const labelTypes = useSelector(selectLabelsTypeList) || [];
  const systemCodes = useSelector(selectSystemCodes);
  const maintenanceCategories = useSelector(selectMaintenanceCategories);
  const assemblyCodes = useSelector(selectAssemblyCodes);
  const reasonForRepairCodes = useSelector(selectReasonForRepairCodes);
  const paymentList = useSelector(selectPaymentTypeList) || [];
  const reminderContactTypes = useSelector(selectReminderContactTypes) || [];
  const codeList = useSelector(selectFaultsCodesList);
  const vehicles = useSelector(selectVehicles);
  const users = useSelector(selectUsers);
  const measurements = useSelector(selectPartsMeasurements).measurements;
  const partLocationsList = useSelector(selectPartsLocations);

  const getStoreData = () => {
    switch (asyncLoadOptionType) {
      case AsyncLoadOptionTypeEnum.VEHICLE:
      case AsyncLoadOptionTypeEnum.VEHICLES:
        return { results: vehicleList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.VEHICLE_VENDOR:
        return { results: vehicleVendorList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.ASSIGNED_TO:
      case AsyncLoadOptionTypeEnum.USER:
      case AsyncLoadOptionTypeEnum.CONTACT:
        return { results: userList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.DRIVER:
        return { results: contacts || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.VENDOR:
        return { results: vendorList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.EXPENSE_TYPE:
        return { results: expenseTypeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.LABEL:
      case AsyncLoadOptionTypeEnum.LABELS:
        return { results: labelDevices || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.TELEMATIC_DEVICE:
        return { results: telematiceDevices || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.ROLE:
        return { results: roles || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.GROUP:
        return { results: group || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.OWNERSHIP_GROUP:
        return { results: ownerShipGroup || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.SERVICE_TASK:
        return { results: serviceTaskList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.EQUIPMENT_TYPE:
        return { results: equipmentTypeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.PART_CATEGORY:
        return { results: partCategoryList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.PART_MANUFACTURER:
      case AsyncLoadOptionTypeEnum.MANUFACTURER:
        return { results: partManufacturerList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.VEHICLE_TYPE:
        return { results: vehicleTypeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.VEHICLE_MODEL:
        return { results: vehicleModelList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.VEHICLE_MAKE:
        return { results: vehicleMakeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.FUEL_TYPE:
        return { results: fuelTypeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.ASSEMBLY_CODE:
        return { results: assemblyCodes || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.MAINTENANCE_CATEGORY:
        return { results: maintenanceCategories || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.SYSTEM_CODE:
        return { results: systemCodes || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.REASON_FOR_REPAIR_CODE:
        return { results: reasonForRepairCodes || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.PAYMENT_TYPE:
        return { results: paymentList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.RENEWAL_TYPE:
        return { results: reminderContactTypes || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.CODE:
        return { results: codeList || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.LINKED_VEHICLE:
        return { results: vehicles || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.ASSIGNED_USER:
        return { results: users || [], count: 0, next: null };
      case AsyncLoadOptionTypeEnum.MEASUREMENT_UTNIT:
        return { results: measurements || [], count: 0, next: null };
      default:
        return { results: [], count: 0, next: null };
    }
  };

  const getRequest = <T extends (arg: TAsyncLoadOptionsParams) => any>(
    action: T,
  ) => {
    return async (arg: TAsyncLoadOptionsParams) => {
      return await dispatch(action(arg)).unwrap();
    };
  };

  const dispatchOptions: Record<AsyncLoadOptionType, any> = useMemo(
    () => ({
      vehicle: getRequest(getVehicleList),
      vehicles: getRequest(getVehicleList),
      payment_type: getRequest(getPaymentType),
      assigned_to: getRequest(getUserList),
      vehicle_vendor: getRequest(getVehicleVendorsList),
      labels: getRequest(getLabelsTypeList),
      driver: getRequest(getContactList),
      user: getRequest(getUserList),
      vendor: getRequest(getVendorsList),
      expense_type: getRequest(getExpenseTypeList),
      telematic_device: getRequest(getTelematicDeviceList),
      label: getRequest(getLabelsTypeList),
      group: getRequest(getGroupTypeList),
      ownerShipGroup: getRequest(getOwnerShipGroupList),
      role: getRequest(getRolesList),
      service_task: getRequest(getServiceTasksList),
      equipment_type: getRequest(getEquipmentTypes),
      category: getRequest(getPartsCategories),
      part_manufacturer: getRequest(getPartManufactures),
      vehicle_type: getRequest(getTypeList),
      vehicle_model: getRequest(getModelList),
      vehicle_make: getRequest(getMakeTypeList),
      fuel_type: getRequest(getFuelTypeList),
      system_code: getRequest(getSystemCodes),
      maintenance_category: getRequest(getMaintenanceCategories),
      assembly_code: getRequest(getAssemblyCodes),
      reason_for_repair_code: getRequest(getReasonForRepairCodes),
      contact: getRequest(getUserList),
      renewal_type: getRequest(getReminderConcactTypes),
      code: getRequest(getFaultCodesList),
      linked_vehicle: getRequest(getVehicles),
      assigned_user: getRequest(getUsers),
      measurement_unit: getRequest(getMeasurements),
      manufacturer: getRequest(getPartManufactures),
      location: getRequest(getPartsLocations),
      adress: getRequest(getPartsLocations),
    }),
    [dispatch],
  );

  return { dispatchOptions, getStoreData };
};
